'use client'

import { Button } from '@acko/button'
import { Typography } from '@acko/typography'
import { useEffect, useState } from 'react'

interface StickyFooterClientProps {
  onCheckPriceClick?: () => void
  triggerId?: string
  priceText?: string
  priceScale?: 'sm' | 'base'
  showOnDesktop?: boolean
}

export const StickyFooterClient = ({
  onCheckPriceClick,
  triggerId = 'cover-options',
  priceText = 'Starting at ₹36/year',
  priceScale = 'base',
  showOnDesktop = false,
}: StickyFooterClientProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const triggerElement = document.getElementById(triggerId)
      const ctaElement = document.getElementById('mobile-sticky-footer-stop')

      if (triggerElement && (isMobile || showOnDesktop)) {
        const triggerThreshold = triggerElement.offsetTop - 100
        const hasScrolledPastTrigger = currentScrollY > triggerThreshold
        const isScrollingDown = currentScrollY > lastScrollY
        const scrollDelta = Math.abs(currentScrollY - lastScrollY)

        const documentHeight = document.documentElement.scrollHeight
        const windowHeight = window.innerHeight
        const scrolledToBottom = currentScrollY + windowHeight >= documentHeight - 200
        const ctaRect = ctaElement?.getBoundingClientRect()
        const isCtaVisible = !!ctaRect && ctaRect.top < windowHeight && ctaRect.bottom > 0

        if (isCtaVisible || scrolledToBottom) {
          setIsVisible(false)
        } else if (hasScrolledPastTrigger && isScrollingDown && scrollDelta > 5) {
          setIsVisible(true)
        } else if (!isScrollingDown && scrollDelta > 5) {
          setIsVisible(false)
        }
      }

      setLastScrollY(currentScrollY)
    }

    if (isMobile || showOnDesktop) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        window.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', checkMobile)
      }
    }

    return () => window.removeEventListener('resize', checkMobile)
  }, [lastScrollY, isMobile, showOnDesktop, triggerId])

  if (!isMobile && !showOnDesktop) {
    return null
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 bg-white border-t border-[var(--color-border-soft)] z-50 transform transition-transform duration-300 ease-in-out ${
        showOnDesktop ? '' : 'lg:hidden'
      } ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{
        boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.1)',
      }}>
      <div
        className={`px-16 md:px-32 py-12 flex items-center justify-between gap-12 ${
          showOnDesktop ? 'w-full max-w-[1280px] mx-auto box-border wide:box-content lg:px-[80px]' : ''
        }`}>
        <div className="flex-1">
          <Typography scale="base" emphasis="bold" color="primary" className="text-[16px] lg:text-[20px] leading-tight">
            Get your bike insurance quote
          </Typography>
          <Typography scale={priceScale} color="secondary" className="leading-tight">
            {priceText}
          </Typography>
        </div>
        <Button type="button" variant="primary" size="md" className="shrink-0 px-20 py-8" onClick={onCheckPriceClick}>
          Get Quote
        </Button>
      </div>
    </div>
  )
}
